// Stale-while-revalidate for Files' Drive reads (Karan 06/10/2026: "it takes
// ages to load drive").
//
// Every visit to Files used to read every chosen project's whole folder tree
// from Drive before painting a row. What Drive said last time is almost always
// what it says now, so the screen paints THAT at once, asks Drive again in the
// background, and repaints when the answer lands. The figure, the to-fix list
// and the folders are therefore never wait-for-Drive.
//
// Storage is IndexedDB (a project's tree is tens of kilobytes; localStorage's
// 5 MB is shared with the rest of Track), with an in-memory layer in front so a
// tab switch inside one visit costs nothing. Keys carry the signed-in email, so
// one person never sees another's Drive on a shared machine, and sign-out
// deletes the store outright (auth.js → clearDriveCache).
//
// Only metadata Drive already showed this person is kept: names, ids, dates,
// sizes. Never file contents.

const DB = 'track-drive-cache';
const STORE = 'kv';
const mem = new Map();
let dbp = null;

function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!dbp) {
    dbp = new Promise((resolve) => {
      try {
        const r = indexedDB.open(DB, 1);
        r.onupgradeneeded = () => r.result.createObjectStore(STORE);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => resolve(null);
        r.onblocked = () => resolve(null);
      } catch (_) { resolve(null); }
    });
  }
  return dbp;
}

function tx(mode, fn) {
  return openDb().then((db) => new Promise((resolve) => {
    if (!db) { resolve(undefined); return; }
    try {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(req && req.result);
      t.onerror = () => resolve(undefined);
      t.onabort = () => resolve(undefined);
    } catch (_) { resolve(undefined); }
  }));
}

/** The default store: memory first, IndexedDB behind it. Never throws. */
export const driveStore = {
  async get(key) {
    if (mem.has(key)) return mem.get(key);
    const v = await tx('readonly', (s) => s.get(key));
    if (v) mem.set(key, v);
    return v || null;
  },
  async set(key, entry) {
    mem.set(key, entry);
    await tx('readwrite', (s) => s.put(entry, key));
  },
};

/** Sign-out: forget everything, in memory and on disk. */
export function clearDriveCache() {
  mem.clear();
  const pending = dbp;
  dbp = null;
  return Promise.resolve(pending).then((db) => {
    try { if (db) db.close(); } catch (_) { /* already closed */ }
    return new Promise((resolve) => {
      try {
        if (typeof indexedDB === 'undefined') { resolve(); return; }
        const r = indexedDB.deleteDatabase(DB);
        r.onsuccess = r.onerror = r.onblocked = () => resolve();
      } catch (_) { resolve(); }
    });
  });
}

/** A per-person key: `who` is the signed-in email. */
export const driveKey = (who, ...parts) => [String(who || 'anon').toLowerCase(), ...parts].join('|');

/**
 * Stale-while-revalidate. Calls onData(value, { cached, at, revalidating }) with
 * what is stored (if anything), then — unless that is younger than `fresh` ms
 * (revalidating: false) — fetches,
 * stores and calls onData again with the live answer. A failed fetch with a
 * stored value keeps the stored value and reports through onError; with none,
 * it throws. Returns the last value given to onData.
 */
export async function swr({ key, fetch, onData = () => {}, onError = () => {}, store = driveStore, now = Date.now, fresh = 30000 }) {
  let hit = null;
  try { hit = await store.get(key); } catch (_) { hit = null; }
  if (hit && 'value' in hit) {
    const stale = now() - hit.at >= fresh;
    onData(hit.value, { cached: true, at: hit.at, revalidating: stale });
    if (!stale) return hit.value;
  }
  try {
    const value = await fetch();
    const at = now();
    try { await store.set(key, { value, at }); } catch (_) { /* a full disk is not a failed read */ }
    onData(value, { cached: false, at });
    return value;
  } catch (e) {
    if (hit && 'value' in hit) { onError(e, { at: hit.at }); return hit.value; }
    throw e;
  }
}

/**
 * swr for several keys answered by ONE fetch (Files reads every chosen
 * project's tree in a single level-by-level walk). onData(values[], { cached,
 * ats[] }) first with whatever is stored (undefined where nothing is), then —
 * if any key is missing or older than `fresh` — with fetchAll()'s array, which
 * is stored key by key (an undefined entry is not stored). A failed fetchAll
 * keeps what was stored and reports through onError; with nothing stored it
 * throws.
 */
export async function swrMany({ keys, fetchAll, onData = () => {}, onError = () => {}, store = driveStore, now = Date.now, fresh = 30000 }) {
  const hits = await Promise.all(keys.map((k) => Promise.resolve().then(() => store.get(k)).catch(() => null)));
  const has = (h) => h && 'value' in h;
  const vals = hits.map((h) => (has(h) ? h.value : undefined));
  const any = hits.some(has);
  const allFresh = keys.length > 0 && hits.every((h) => has(h) && now() - h.at < fresh);
  if (any) onData(vals, { cached: true, ats: hits.map((h) => (has(h) ? h.at : null)), revalidating: !allFresh });
  if (allFresh) return vals;
  try {
    const live = await fetchAll();
    const at = now();
    await Promise.all(keys.map((k, i) => (live[i] === undefined ? null
      : Promise.resolve().then(() => store.set(k, { value: live[i], at })).catch(() => {}))));
    onData(live, { cached: false, ats: keys.map(() => at) });
    return live;
  } catch (e) {
    if (any) { onError(e, { ats: hits.map((h) => (has(h) ? h.at : null)) }); return vals; }
    throw e;
  }
}

/**
 * Store folder listings learnt in passing — a one-call count of every row on
 * screen (listChildren) is every one of those folders' listing — under the
 * same keys files-folder.js and files-browse.js read, so opening one paints at
 * once. `map` is Map folderId → children. Best effort.
 */
export function seedCache(who, map, { store = driveStore, now = Date.now } = {}) {
  const at = now();
  const jobs = [];
  (map || new Map()).forEach((children, id) => {
    jobs.push(Promise.resolve().then(() => store.set(driveKey(who, 'list', id), { value: children, at })).catch(() => {}));
  });
  return Promise.all(jobs);
}

/** "just now", "4 min ago", "2 h ago", "yesterday", "3 days ago". */
export function ageText(at, now = Date.now()) {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}
